import { appI18n } from '@flowus/common/i18n';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

const ONE_MINUTE_IN_MS = 1000 * 60;
const ONE_HOUR_IN_MS = ONE_MINUTE_IN_MS * 60;
const ONE_DAY_IN_MS = ONE_HOUR_IN_MS * 24;

export function useTimeAgoText() {
  const { t } = useAppTranslation();
  return (timestamp: number, now: number) => {
    const distance = now - timestamp;
    if (distance <= ONE_MINUTE_IN_MS) {
      return t('common.utils.just_now');
    }
    if (distance <= ONE_HOUR_IN_MS) {
      return t('common.time_ago.minutes_ago', { count: Math.floor(distance / ONE_MINUTE_IN_MS) });
    }
    if (distance <= ONE_DAY_IN_MS) {
      return t('common.time_ago.hours_ago', { count: Math.floor(distance / ONE_HOUR_IN_MS) });
    }

    const time = dayjs(timestamp);
    if (time.isSame(now, 'year')) {
      return time.format(t('common.time_ago.format_same_year') as string);
    }

    return time.format(t('common.time_ago.format_other_year') as string);
  };
}

// 保留原函数以兼容现有代码
export function timeAgoText(timestamp: number, now: number) {
  const distance = now - timestamp;
  if (distance <= ONE_MINUTE_IN_MS) {
    return appI18n.t('common.utils.just_now') as string;
  }
  if (distance <= ONE_HOUR_IN_MS) {
    return appI18n.t('common.time_ago.minutes_ago', {
      count: Math.floor(distance / ONE_MINUTE_IN_MS),
    }) as string;
  }
  if (distance <= ONE_DAY_IN_MS) {
    return appI18n.t('common.time_ago.hours_ago', {
      count: Math.floor(distance / ONE_HOUR_IN_MS),
    }) as string;
  }

  const time = dayjs(timestamp);
  if (time.isSame(now, 'year')) {
    return time.format(appI18n.t('common.time_ago.format_same_year') as string);
  }

  return time.format(appI18n.t('common.time_ago.format_other_year') as string);
}

function computeDistanceToNextUpdate(timestamp: number, now: number, delayInMs = 200) {
  const distance = now - timestamp;
  if (distance <= ONE_MINUTE_IN_MS) {
    return timestamp + ONE_MINUTE_IN_MS - now + delayInMs;
  }
  if (distance <= ONE_HOUR_IN_MS) {
    return ONE_MINUTE_IN_MS - (distance % ONE_MINUTE_IN_MS) + delayInMs;
  }
  if (distance <= ONE_DAY_IN_MS) {
    return ONE_HOUR_IN_MS - (distance % ONE_HOUR_IN_MS) + delayInMs;
  }
  // 一年就算了吧
  return null;
}

export const TimeAgo: FC<{ timestamp: number }> = ({ timestamp }) => {
  const [version, setVersion] = useState(0);
  const getTimeAgoText = useTimeAgoText();
  useEffect(() => {
    const distanceToNextUpdate = computeDistanceToNextUpdate(timestamp, Date.now());
    if (distanceToNextUpdate == null) return;
    const timer = setTimeout(() => {
      setVersion((version) => version + 1);
    }, distanceToNextUpdate);
    return () => clearTimeout(timer);
  }, [setVersion, version, timestamp]);
  return <>{getTimeAgoText(timestamp, Date.now())}</>;
};
